import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import type {
    MaintenanceOperationDto,
    MaintenanceRunDetailsDto,
    MaintenanceRunDto
} from '../../types';
import './styles.css';

const formatDateTime = (value: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('ru-RU');
};

const formatDuration = (run: MaintenanceRunDto | MaintenanceRunDetailsDto) => {
    if (!run.startedAt || !run.completedAt) return '-';

    const startedAt = new Date(run.startedAt).getTime();
    const completedAt = new Date(run.completedAt).getTime();
    const seconds = Math.max(0, Math.round((completedAt - startedAt) / 1000));

    return `${seconds} сек.`;
};

const parseJson = <T,>(value: string | null): T | null => {
    if (!value) return null;

    try {
        return JSON.parse(value) as T;
    } catch {
        return null;
    }
};

const statusLabel: Record<string, string> = {
    Queued: 'В очереди',
    Running: 'Выполняется',
    Succeeded: 'Успешно',
    Failed: 'Ошибка'
};

const parseRefreshInterval = (value: string | null) => {
    if (!value) return 3;

    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 3;
};

const MaintenanceManagement: React.FC = () => {
    const [operations, setOperations] = useState<MaintenanceOperationDto[]>([]);
    const [runs, setRuns] = useState<MaintenanceRunDto[]>([]);
    const [selectedRun, setSelectedRun] = useState<MaintenanceRunDetailsDto | null>(null);
    const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [runLoading, setRunLoading] = useState<string | null>(null);
    const [refreshInterval, setRefreshInterval] = useState<number>(() => {
        return parseRefreshInterval(localStorage.getItem('maintenanceRefreshInterval'));
    });

    const loadOperations = useCallback(async () => {
        const response = await api.get<MaintenanceOperationDto[]>('/api/maintenance/operations');
        setOperations(response.data);
    }, []);

    const loadRuns = useCallback(async () => {
        const response = await api.get<MaintenanceRunDto[]>('/api/maintenance/runs');
        setRuns(response.data);
        return response.data;
    }, []);

    const loadRunDetails = useCallback(async (runId: number) => {
        const response = await api.get<MaintenanceRunDetailsDto>(`/api/maintenance/runs/${runId}`);
        setSelectedRun(response.data);
        setSelectedRunId(runId);
    }, []);

    const loadInitialData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([loadOperations(), loadRuns()]);
        } finally {
            setLoading(false);
        }
    }, [loadOperations, loadRuns]);

    useEffect(() => {
        void loadInitialData();
    }, [loadInitialData]);

    const handleRun = async (operation: MaintenanceOperationDto) => {
        const confirmed = window.confirm(`Запустить операцию «${operation.title}»?`);
        if (!confirmed) return;

        setRunLoading(operation.code);
        try {
            const response = await api.post<MaintenanceRunDto>(
                `/api/maintenance/operations/${operation.code}/run`);
            toast.success('Операция поставлена в очередь.');
            await loadRuns();
            await loadRunDetails(response.data.id);
        } finally {
            setRunLoading(null);
        }
    };

    const handleRefreshRuns = useCallback(async () => {
        await loadRuns();
        if (selectedRunId) {
            await loadRunDetails(selectedRunId);
        }
    }, [loadRunDetails, loadRuns, selectedRunId]);

    const handleOpenRunLog = async (runId?: number) => {
        const loadedRuns = await loadRuns();
        const runIdToSelect = runId
            ?? (selectedRunId && loadedRuns.some(run => run.id === selectedRunId)
            ? selectedRunId
            : loadedRuns[0]?.id);

        if (runIdToSelect) {
            await loadRunDetails(runIdToSelect);
        }

        setIsHistoryOpen(true);
    };

    useEffect(() => {
        localStorage.setItem('maintenanceRefreshInterval', refreshInterval.toString());
    }, [refreshInterval]);

    useEffect(() => {
        if (refreshInterval <= 0) return;

        const intervalId = window.setInterval(() => {
            void handleRefreshRuns();
        }, refreshInterval * 1000);

        return () => window.clearInterval(intervalId);
    }, [handleRefreshRuns, refreshInterval]);

    const selectedReport = parseJson<Record<string, unknown>>(selectedRun?.reportJson ?? null);
    const selectedSummary = parseJson<Record<string, unknown>>(selectedRun?.summaryJson ?? null);
    const latestRun = runs[0] ?? null;

    if (loading) {
        return (
            <div className="maintenance-page">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="maintenance-page">
            <div className="maintenance-header">
                <div>
                    <h1>Обслуживание</h1>
                    <p>Ручной запуск технических операций.</p>
                </div>
                <div className="maintenance-header-controls">
                    {latestRun && (
                        <div className="maintenance-last-run">
                            <span>Последний запуск</span>
                            <strong>#{latestRun.id}</strong>
                            <span className={`maintenance-status status-${latestRun.status.toLowerCase()}`}>
                                {statusLabel[latestRun.status] ?? latestRun.status}
                            </span>
                        </div>
                    )}
                    <button
                        className="btn btn-outline-secondary"
                        disabled={runs.length === 0}
                        onClick={() => handleOpenRunLog(latestRun?.id)}
                    >
                        <i className="bi bi-journal-text"></i>
                        Открыть журнал
                    </button>
                </div>
            </div>

            <section className="maintenance-section">
                <h2>Операции</h2>
                <div className="maintenance-operation-list">
                    {operations.map(operation => (
                        <div className="maintenance-operation" key={operation.code}>
                            <div>
                                <h3>{operation.title}</h3>
                                <p>{operation.description}</p>
                            </div>
                            <div className="maintenance-actions">
                                <button
                                    className="btn btn-primary"
                                    disabled={runLoading === operation.code}
                                    onClick={() => handleRun(operation)}
                                >
                                    {runLoading === operation.code ? 'Запуск...' : 'Запустить'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="maintenance-section maintenance-runs-section">
                <div className="maintenance-section-header">
                    <h2>Последние запуски</h2>
                    <div className="maintenance-history-toolbar">
                        <label className="maintenance-refresh-control">
                            <span>Обновлять</span>
                            <select
                                value={refreshInterval}
                                onChange={(event) => setRefreshInterval(parseInt(event.target.value, 10))}
                            >
                                <option value={0}>не нужно</option>
                                <option value={3}>каждые 3 сек</option>
                                <option value={5}>каждые 5 сек</option>
                                <option value={10}>каждые 10 сек</option>
                                <option value={30}>каждые 30 сек</option>
                            </select>
                        </label>
                        <button className="btn btn-outline-secondary" onClick={handleRefreshRuns}>
                            <i className="bi bi-arrow-clockwise"></i>
                            Обновить
                        </button>
                    </div>
                </div>
                {runs.length > 0 ? (
                    <div className="maintenance-table-wrap">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Операция</th>
                                    <th>Статус</th>
                                    <th>Запущено</th>
                                    <th>Длительность</th>
                                    <th>Итог</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map(run => {
                                    const summary = parseJson<{ attributesBound?: number; categoriesTouched?: number }>(run.summaryJson);
                                    return (
                                        <tr
                                            key={run.id}
                                            className={selectedRunId === run.id ? 'table-active' : ''}
                                            onClick={() => handleOpenRunLog(run.id)}
                                        >
                                            <td>{run.id}</td>
                                            <td>{run.operationTitle}</td>
                                            <td>
                                                <span className={`maintenance-status status-${run.status.toLowerCase()}`}>
                                                    {statusLabel[run.status] ?? run.status}
                                                </span>
                                            </td>
                                            <td>{formatDateTime(run.createdAt)}</td>
                                            <td>{formatDuration(run)}</td>
                                            <td>
                                                {run.errorMessage
                                                    ? run.errorMessage
                                                    : summary
                                                        ? `${summary.attributesBound ?? 0} связей, ${summary.categoriesTouched ?? 0} категорий`
                                                        : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="maintenance-empty">Запусков пока нет.</div>
                )}
            </section>

            {isHistoryOpen && (
                <div className="modal-backdrop-custom" onClick={() => setIsHistoryOpen(false)}>
                    <div className="modal-dialog-custom maintenance-history-modal animate-in" onClick={(event) => event.stopPropagation()}>
                        <div className="modal-content-custom">
                            <div className="modal-header-custom">
                                <div>
                                    <h3 className="modal-title-custom">
                                        {selectedRun ? `Журнал и отчет #${selectedRun.id}` : 'Журнал и отчет'}
                                    </h3>
                                    <p className="modal-subtitle-custom">
                                        {selectedRun?.operationTitle ?? 'Операция обслуживания'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close-custom"
                                    aria-label="Закрыть"
                                    onClick={() => setIsHistoryOpen(false)}
                                />
                            </div>
                            <div className="modal-body-custom maintenance-history-body">
                                {selectedRun ? (
                                    <section className="maintenance-section">
                                        <div className="maintenance-run-details">
                                            <div><strong>Статус:</strong> {statusLabel[selectedRun.status] ?? selectedRun.status}</div>
                                            <div><strong>Hangfire:</strong> {selectedRun.hangfireJobId ?? '-'}</div>
                                            <div><strong>Запустил:</strong> {selectedRun.requestedByUserName ?? '-'}</div>
                                            <div><strong>Начало:</strong> {formatDateTime(selectedRun.startedAt)}</div>
                                            <div><strong>Завершение:</strong> {formatDateTime(selectedRun.completedAt)}</div>
                                            <div><strong>Длительность:</strong> {formatDuration(selectedRun)}</div>
                                        </div>

                                        {selectedSummary && (
                                            <pre className="maintenance-json">{JSON.stringify(selectedSummary, null, 2)}</pre>
                                        )}

                                        {selectedReport && (
                                            <pre className="maintenance-json">{JSON.stringify(selectedReport, null, 2)}</pre>
                                        )}

                                        <h3>События</h3>
                                        <div className="maintenance-log-list">
                                            {selectedRun.logs.length > 0 ? selectedRun.logs.map(log => (
                                                <div className="maintenance-log-row" key={log.id}>
                                                    <span>{formatDateTime(log.createdAt)}</span>
                                                    <strong>{log.level}</strong>
                                                    <span>{log.message}</span>
                                                </div>
                                            )) : (
                                                <div className="maintenance-empty">Событий нет.</div>
                                            )}
                                        </div>
                                    </section>
                                ) : (
                                    <div className="maintenance-empty">Запусков пока нет.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaintenanceManagement;
